// THE FILTERS DIALOG (owner, 2026-10-09 — D-344: "Where the Filter button is
// present, please use it to launch a proper Filters modal, centered on the
// screen. The little thing in the corner is a pain to use. While doing so,
// please check that the dropdowns behave normally. I am having issues with
// scrolling and closing them in the current state.").
//
// Every Filter button — the Invoices pages' (`FilterButton`), the lists on
// References, Rules, Assignments and Notifications (`ListFilters`), and the
// Dashboard's (`DashboardFilterButton`) — opens this one centered dialog. Each
// was a `Popover` hung off the button in the header's corner until then.
//
// WHY THE DROPDOWNS MISBEHAVED, and the fix. A `Dropdown` or `Combobox` listbox
// portals to `document.body` by default, outside the popover's focus trap, so
// the trap hauled focus back after each click and the list snapped shut (the
// gotcha `app/README.md` records for `PopoverSurface`). The children here are
// handed the dialog surface as `host`, and every list inside mounts there
// through `popupPositionProps` — inside the trap, above the fields, measured
// against the window, with its own scrolling height — which is how the app's
// other dialogs already keep their pickers working.
//
// ONE FOOTER, ONE RULE FOR APPLYING (owner, 2026-10-09: "the blue button should
// be Apply, which both applies the setting and closes the modal. Reset should be
// button with the same structure but not filled blue ... Closing without saving
// can be done by clicking outside the modal. Setting should not be applied
// unless the Apply button is pressed. Every Filter modal should share this
// structure."). Each caller edits a draft; Apply commits it and closes; Close,
// a click outside or Escape discards it. RESET SITS AT THE LEFT, apart from the
// two that leave (owner, 2026-10-09: "change the current Reset button to a
// Close button ... Then, let's add a new Reset button that is inline there but
// left-aligned"); it returns the draft to the default view and applies nothing.
//
// The body follows the pop-up form spacing standard (`dialogForm.ts`, D-346).
import { useState, type ReactNode } from 'react'
import {
  Button,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  makeStyles,
  mergeClasses,
} from '@fluentui/react-components'
import { AppDialog } from './AppDialog'
import { useDialogFormStyles } from './dialogForm'

const useStyles = makeStyles({
  surface: { width: '520px', maxWidth: 'calc(100vw - 32px)' },
  // The Dashboard's fifteen criteria sit two to a row.
  wide: { width: '720px', maxWidth: 'calc(100vw - 32px)' },
})

interface Props {
  open: boolean
  onClose: () => void
  /** Two controls a row, for the Dashboard. */
  wide?: boolean
  /** Empty the draft. Nothing is applied until Apply. */
  onReset: () => void
  /** Commit the draft. The dialog closes itself after. */
  onApply: () => void
  /** The filters. `host` is the dialog surface, for every list to mount in. */
  children: (host: HTMLElement | null) => ReactNode
}

export function FilterDialog({ open, onClose, wide = false, onReset, onApply, children }: Props) {
  const s = useStyles()
  const f = useDialogFormStyles()
  const [host, setHost] = useState<HTMLDivElement | null>(null)
  return (
    <AppDialog open={open} onOpenChange={(_, d) => !d.open && onClose()}>
      <DialogSurface ref={setHost} className={wide ? s.wide : s.surface}>
        <DialogBody>
          <DialogTitle>Filters</DialogTitle>
          <DialogContent className={mergeClasses(f.stack, f.beforeActions)}>{children(host)}</DialogContent>
          <DialogActions position="start">
            <Button appearance="secondary" onClick={onReset}>
              Reset
            </Button>
          </DialogActions>
          <DialogActions position="end">
            <Button appearance="secondary" onClick={onClose}>
              Close
            </Button>
            <Button
              appearance="primary"
              onClick={() => {
                onApply()
                onClose()
              }}
            >
              Apply
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </AppDialog>
  )
}
